import React from "react"
import { Phone, X, Plus, CreditCard, CheckCircle2 } from "lucide-react"
import { ChatbotModal } from "../ChatbotModal"
import { ErrorModal } from "../ErrorModal"

interface DashboardModalsProps {
    showChatbot: boolean;
    setShowChatbot: (show: boolean) => void;
    showErrorModal: boolean;
    setShowErrorModal: (show: boolean) => void;
    errorModalMessage: string;
    isTakeAway: boolean | null;
    showWaiterModal: boolean;
    setShowWaiterModal: (show: boolean) => void;
    confirmCallWaiter: () => void;
    showCancelWaiterModal: boolean;
    setShowCancelWaiterModal: (show: boolean) => void;
    handleCancelWaiter: () => void;
    showPendingCollectionOrderModal: boolean;
    setShowPendingCollectionOrderModal: (show: boolean) => void;
    confirmPendingCollectionOrder: () => void;
    showPendingCollectionPaymentModal: boolean;
    setShowPendingCollectionPaymentModal: (show: boolean) => void;
    isSessionClosed: boolean;
    isTakeAwaySessionClosed: boolean;
}

export const DashboardModals: React.FC<DashboardModalsProps> = ({
    showChatbot,
    setShowChatbot,
    showErrorModal,
    setShowErrorModal,
    errorModalMessage,
    isTakeAway,
    showWaiterModal,
    setShowWaiterModal,
    confirmCallWaiter,
    showCancelWaiterModal,
    setShowCancelWaiterModal,
    handleCancelWaiter,
    showPendingCollectionOrderModal,
    setShowPendingCollectionOrderModal,
    confirmPendingCollectionOrder,
    showPendingCollectionPaymentModal,
    setShowPendingCollectionPaymentModal,
    isSessionClosed,
    isTakeAwaySessionClosed
}) => {
    return (
        <>
            {showChatbot && <ChatbotModal isOpen={showChatbot} onClose={() => setShowChatbot(false)} />}

            {showErrorModal && (
                <ErrorModal
                    isOpen={showErrorModal}
                    onClose={() => setShowErrorModal(false)}
                    title="Error"
                    message={errorModalMessage}
                    showContactWaiter={!isTakeAway}
                />
            )}

            {/* Waiter Call Confirmation Modal */}
            {showWaiterModal && !isTakeAway && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                    <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-lg rounded-3xl p-8 max-w-sm w-full shadow-2xl border border-gray-100/50 dark:border-gray-700/50">
                        <div className="text-center">
                            <div className="w-20 h-20 bg-gradient-to-r from-blue-100 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-3xl flex items-center justify-center mx-auto mb-6">
                                <Phone className="w-10 h-10 text-blue-600 dark:text-blue-400" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">¿Llamar al mozo?</h3>
                            <p className="text-gray-600 dark:text-gray-400 mb-8">El mozo será notificado y se dirigirá a tu mesa</p>
                            <div className="flex gap-4">
                                <button
                                    onClick={() => setShowWaiterModal(false)}
                                    className="flex-1 bg-gray-100/80 dark:bg-gray-700/80 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-900 dark:text-white py-4 rounded-2xl font-semibold transition-all duration-300 backdrop-blur-sm"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={confirmCallWaiter}
                                    className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white py-4 rounded-2xl font-semibold transition-all duration-300 shadow-lg"
                                >
                                    Llamar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Cancel Waiter Call Modal */}
            {showCancelWaiterModal && !isTakeAway && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                    <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-lg rounded-3xl p-8 max-w-sm w-full shadow-2xl border border-gray-100/50 dark:border-gray-700/50">
                        <div className="text-center">
                            <div className="w-20 h-20 bg-gradient-to-r from-sky-100 to-pink-100 dark:from-sky-900/20 dark:to-pink-900/20 rounded-3xl flex items-center justify-center mx-auto mb-6">
                                <X className="w-10 h-10 text-sky-600 dark:text-sky-400" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">¿Cancelar llamado?</h3>
                            <p className="text-gray-600 dark:text-gray-400 mb-8">Se cancelará la notificación al mozo</p>
                            <div className="flex gap-4">
                                <button
                                    onClick={() => setShowCancelWaiterModal(false)}
                                    className="flex-1 bg-gray-100/80 dark:bg-gray-700/80 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-900 dark:text-white py-4 rounded-2xl font-semibold transition-all duration-300 backdrop-blur-sm"
                                >
                                    Mantener
                                </button>
                                <button
                                    onClick={handleCancelWaiter}
                                    className="flex-1 bg-gradient-to-r from-sky-500 to-pink-500 hover:from-sky-600 hover:to-pink-600 text-white py-4 rounded-2xl font-semibold transition-all duration-300 shadow-lg"
                                >
                                    Cancelar Llamado
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Pending Collection Order Modal - When clicking "Ordenar" */}
            {showPendingCollectionOrderModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                    <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-lg rounded-3xl p-8 max-w-sm w-full shadow-2xl border border-gray-100/50 dark:border-gray-700/50">
                        <div className="text-center">
                            <div className="w-20 h-20 bg-gradient-to-r from-yellow-100 to-orange-100 dark:from-yellow-900/20 dark:to-orange-900/20 rounded-3xl flex items-center justify-center mx-auto mb-6">
                                <Plus className="w-10 h-10 text-yellow-600 dark:text-yellow-400" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">¿Ordenar más productos?</h3>
                            <p className="text-gray-600 dark:text-gray-400 mb-8">
                                Seleccionaste pagar en el local. Si ordenas algo, se sumará a tu cuenta actual.
                            </p>
                            <div className="flex gap-4">
                                <button
                                    onClick={() => setShowPendingCollectionOrderModal(false)}
                                    className="flex-1 bg-gray-100/80 dark:bg-gray-700/80 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-900 dark:text-white py-4 rounded-2xl font-semibold transition-all duration-300 backdrop-blur-sm"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={confirmPendingCollectionOrder}
                                    className="flex-1 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white py-4 rounded-2xl font-semibold transition-all duration-300 shadow-lg"
                                >
                                    Aceptar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Pending Collection Payment Modal - When clicking "Pagar" */}
            {showPendingCollectionPaymentModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                    <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-lg rounded-3xl p-8 max-w-sm w-full shadow-2xl border border-gray-100/50 dark:border-gray-700/50">
                        <div className="text-center">
                            <div className="w-20 h-20 bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900/20 dark:to-emerald-900/20 rounded-3xl flex items-center justify-center mx-auto mb-6">
                                <CreditCard className="w-10 h-10 text-green-600 dark:text-green-400" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Seleccionaste pagar en el local</h3>
                            <p className="text-gray-600 dark:text-gray-400 mb-8">
                                ¡Se te cobrara al entregarte!
                            </p>
                            <div className="flex justify-center">
                                <button
                                    onClick={() => setShowPendingCollectionPaymentModal(false)}
                                    className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white py-4 rounded-2xl font-semibold transition-all duration-300 shadow-lg"
                                >
                                    Aceptar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Session Closed Modal/Banner for TakeAway */}
            {isSessionClosed && isTakeAwaySessionClosed && (
                <div className="fixed inset-x-0 bottom-0 z-50 bg-white dark:bg-gray-900 rounded-t-3xl shadow-[0_-4px_20px_rgba(0,0,0,0.2)] border-t border-gray-200 dark:border-gray-800 p-6 animate-in slide-in-from-bottom duration-500">
                    <div className="flex flex-col items-center text-center">
                        <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
                            <CheckCircle2 className="w-8 h-8 text-green-600 dark:text-green-400" />
                        </div>
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                            ¡Pedido Finalizado!
                        </h2>
                        <p className="text-gray-600 dark:text-gray-400 mb-6">
                            Tu pedido ha sido pagado y confirmado. ¡Que lo disfrutes!
                        </p>

                        <div className="w-full space-y-3">
                            <a
                                href="https://comandaya.app/valoraciones"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block w-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-900 dark:text-white py-3 rounded-xl font-semibold transition-colors"
                            >
                                Valorar experiencia
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
